import { i18n } from '@/i18n';

import { AuthGuard } from '@/auth/auth-guards';
import { invitationsMessages } from '@/components/Activations/Layout/ActivationTabsMessages';
import { activationRoutes } from '@/views/activation/activation-routes';
import { adminRoutes } from '@/views/admin/admin-routes';
import { backOfficeMessages } from '@/views/gallery/back-office-messages';
import { getRoute, RoutePaths } from '@/views/gallery/route-paths';
import { sideNavMessages } from '@/views/gallery/side-nav.messages';
import { inventoryRoutes } from '@/views/inventory/inventory-routes';
import { Permission } from '@art-basel/artbasel-lib-platform';
import type { RouteLocation, RouteRecordRaw } from 'vue-router';
import { linkInBioRoutes } from '../linkInBio/link-in-bio-routes';
import { salesAgreementRoutes } from './sales-agreement/salesAgreement-routes';

const { t } = i18n.global;

export const backOfficeRoutes: Array<RouteRecordRaw> = [
  {
    meta: { title: t('platform.app.title') },
    path: RoutePaths.ROOT,
    component: () => import(`./Home.vue`),
    children: [
      {
        meta: { title: invitationsMessages.dashboardTitle },
        path: '',
        redirect: getRoute(RoutePaths.DASHBOARD),
      },
      {
        meta: { title: invitationsMessages.dashboardTitle },
        path: RoutePaths.DASHBOARD,
        component: () => import(`./invitations/InvitationsDashboard.vue`),
        beforeEnter: (to: RouteLocation) => AuthGuard(to),
      },
      {
        meta: { title: invitationsMessages.detailsTitle },
        path: RoutePaths.INVITATIONS,
        component: () => import(`./invitations/InvitationDetails.vue`),
        beforeEnter: (to: RouteLocation) => AuthGuard(to),
      },
      ...inventoryRoutes,
      ...activationRoutes,
      ...linkInBioRoutes,
      ...adminRoutes,
      {
        meta: { title: backOfficeMessages.sellerPurchaseRequestsTitle },
        path: RoutePaths.SELL.REQUESTS,
        component: () => import(`./purchase-requests/purchase-request-lists/PurchaseRequestsSellerList.vue`),
        beforeEnter: (to: RouteLocation) => AuthGuard(to, Permission.READ_PURCHASE_REQUESTS),
      },
      {
        meta: { title: t('platform.app.purchaseRequest.purchaseRequestDetailTitle') },
        path: RoutePaths.SELL.REQUEST_DETAIL,
        component: () => import(`./purchase-requests/PurchaseRequestDetails.vue`),
        beforeEnter: (to: RouteLocation) => AuthGuard(to, Permission.READ_PURCHASE_REQUESTS),
      },
      {
        meta: { title: backOfficeMessages.buyerPurchaseRequestsTitle },
        path: RoutePaths.BUY.REQUESTS,
        component: () => import(`./purchase-requests/purchase-request-lists/PurchaseRequestsBuyerList.vue`),
        beforeEnter: (to: RouteLocation) => AuthGuard(to),
      },
      ...salesAgreementRoutes,
      {
        meta: { title: sideNavMessages.account },
        path: RoutePaths.ACCOUNT.ROOT,
        children: [
          {
            path: '',
            redirect: getRoute(RoutePaths.ACCOUNT.SETTINGS),
          },
          {
            meta: { title: sideNavMessages.accountSettings },
            path: RoutePaths.SETTINGS,
            beforeEnter: (to: RouteLocation) => AuthGuard(to),
            component: () => import('./account/settings/AccountSettings.vue'),
          },
          {
            meta: { title: sideNavMessages.mySettings },
            path: RoutePaths.MY_SETTINGS,
            beforeEnter: (to: RouteLocation) => AuthGuard(to),
            component: () => import('./account/settings/MySettings.vue'),
          },
          {
            meta: { title: sideNavMessages.subscription },
            path: RoutePaths.SUBSCRIPTION,
            beforeEnter: (to: RouteLocation) => AuthGuard(to),
            component: () => import('./account/subscription/Subscription.vue'),
          },
          {
            meta: { title: sideNavMessages.accountPreferences },
            path: RoutePaths.PREFERENCES,
            beforeEnter: (to: RouteLocation) => AuthGuard(to),
            component: () => import('./account/preferences/Preferences.vue'),
          },
          {
            meta: { title: sideNavMessages.accountTeam },
            path: 'team',
            component: () => import('./account/team/Team.vue'),
            beforeEnter: (to: RouteLocation) => AuthGuard(to, Permission.READ_TEAM),
          },
        ],
      },
      {
        meta: { title: t('platform.component.paymentOnboarding.title') },
        path: RoutePaths.PAYMENT_ONBOARDING,
        component: () => import(`./payment/PaymentOnboarding.vue`),
        beforeEnter: (to: RouteLocation) => AuthGuard(to),
      },
      // TODO: Remove legacy route redirects Q3 2025
      {
        meta: { title: backOfficeMessages.artworksTitle },
        path: 'artworks',
        redirect: getRoute(RoutePaths.INVENTORY),
      },
      {
        meta: { title: backOfficeMessages.artworkCreateTitle },
        path: 'artworks/create',
        redirect: getRoute(RoutePaths.INVENTORY_CREATE),
      },
      {
        meta: { title: backOfficeMessages.artworkDetailTitle },
        path: 'artworks/:id',
        redirect: (to) => getRoute(RoutePaths.INVENTORY_DETAIL, to.params.id as string | undefined),
      },
      {
        meta: { title: backOfficeMessages.artworkEditTitle },
        path: 'artworks/:id/edit',
        redirect: (to) => getRoute(RoutePaths.INVENTORY_EDIT, to.params.id as string | undefined),
      },
    ],
  },
];
